import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Divider
} from '@mui/material';
import ExistingRightsDialog from './ExistingRightsDialog';

// --- HELPER COMPONENT FOR ROWS ---
const ModalFormRow = ({ label, children, note }) => (
  <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1.5 }}>
    <Grid item xs={12} md={4}>
      <Typography variant="caption" sx={{ fontWeight: 500, color: '#333', mt: 1, display: 'block' }}>
        {label}
      </Typography>
      {note && (
         <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#666', display: 'block', fontSize: '0.7rem' }}>
             {note}
         </Typography>
      )}
    </Grid>
    <Grid item xs={12} md={8}>
      {children}
    </Grid>
  </Grid>
);

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: 3, bgcolor: '#fdfdfd', minHeight: '550px' }}>
            {children}
        </Box>
      )}
    </div>
  );
}

export default function AddConventionalPropertyDialog({ open, onClose, matterId, onSave, initialData }) {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  // State for the Section 42(2) sub-modal
  const [openExistingRights, setOpenExistingRights] = useState(false);

  const handleOpenExistingRights = () => {
    setOpenExistingRights(true);
  };

  const handleCloseExistingRights = () => {
    setOpenExistingRights(false);
  };

  const handleSaveExistingRights = (rightsData) => {
      setFormData(prev => ({ ...prev, existingRights: rightsData }));
      setOpenExistingRights(false);
  };

  const [formData, setFormData] = useState(initialData || {});

  useEffect(() => {
    if (open) setFormData(initialData || {});
  }, [open, initialData]);

  const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
      }));
  };

  const handleSave = async () => {
      try {
          await axios.post(`https://demo.insollex.com/api/legal-matter/${matterId}/conventional-properties`, formData);
          if (onSave) onSave();
          onClose();
      } catch (error) {
          console.error("Error saving property:", error);
          alert("Failed to save property.");
      }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', py: 1, fontSize: '0.9rem', fontWeight: 'bold' }}>
        Conv Prop
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, bgcolor: '#fdfdfd' }}>
        
        {/* MODAL TABS HEADER */}
        <Paper square elevation={0} sx={{ borderBottom: '1px solid #e0e0e0', bgcolor: '#f0f0f0' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                fontSize: '0.75rem',
                minHeight: 32,
                textTransform: 'none',
                fontWeight: 500,
                py: 0.5,
                borderRight: '1px solid #e0e0e0',
                px: 2
              },
              '& .Mui-selected': { bgcolor: '#fff', fontWeight: 'bold' }
            }}
          >
            <Tab label="Conv Prop" />
            <Tab label="Values" />
            <Tab label="Notes" />
            <Tab label="Est Duty (1)" />
            <Tab label="Est Duty (2)" />
            <Tab label="Estimated CGT" />
          </Tabs>
        </Paper>

        {/* ================= TAB 1: CONV PROP ================= */}
        <TabPanel value={tabValue} index={0}>
            <Stack spacing={0.5}>
                
                <ModalFormRow label="Ownership share (leave BLANK if 100% or if community marriage share)">
                    <TextField 
                        name="ownershipShare"
                        onChange={handleChange}
                        value={formData.ownershipShare || ''}
                        sx={{ width: '150px', bgcolor: '#fffde7', '& .MuiInputBase-input': { py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                        required // Visual indication only
                    />
                </ModalFormRow>

                <ModalFormRow label="Description of Property as it appears in Title Deed" note="and including any amendments made thereto by any Act or Regulation">
                    <TextField 
                        name="description"
                        onChange={handleChange}
                        value={formData.description || ''}
                        fullWidth 
                        multiline 
                        rows={4} 
                        size="small" 
                        variant="outlined" 
                        sx={{ bgcolor: '#fff' }}
                    />
                </ModalFormRow>

                <ModalFormRow label="Number of current Holding Deed (e.g. T1234/89)">
                    <TextField name="holdingDeed" onChange={handleChange} value={formData.holdingDeed || ''} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Short Description of Property (e.g. Erf 10 Pretoria)">
                    <TextField name="shortDescription" onChange={handleChange} value={formData.shortDescription || ''} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Street Address of Property OR farm name">
                    <TextField name="streetAddress" onChange={handleChange} value={formData.streetAddress || ''} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Date Property was originally acquired (if known)">
                    <TextField type="date" InputLabelProps={{ shrink: true }} name="dateAcquired" onChange={handleChange} value={formData.dateAcquired || ''} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Was property owned jointly with surviving spouse?">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox name="isJointlyOwned" onChange={handleChange} checked={formData.isJointlyOwned || false} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(Only where NOT married in community of property)</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Is this a special bequest?">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox name="isSpecialBequest" onChange={handleChange} checked={formData.isSpecialBequest || false} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>(Bequest will be excluded from residue awarded ito the Will)</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="In terms of which clause in Will/Redist. Agreement?">
                    <TextField name="willClause" onChange={handleChange} value={formData.willClause || ''} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="To whom must it be awarded?">
                     <Stack direction="row" spacing={1} sx={{ width: '100%' }} alignItems="flex-start">
                         <TextField name="awardedTo" onChange={handleChange} value={formData.awardedTo || ''} fullWidth multiline rows={3} size="small" variant="outlined" sx={{ bgcolor: '#fff' }} />
                         <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '80px', height: '100%' }}>
                            Heirs
                         </Button>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Is this property situated OUTSIDE the RSA?">
                    <Checkbox name="isOutsideRSA" onChange={handleChange} checked={formData.isOutsideRSA || false} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Is the property awarded to any of the following?">
                    <RadioGroup name="awardedToType" onChange={handleChange} value={formData.awardedToType || 'None'}>
                        <FormControlLabel value="Spouse" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Surviving spouse</Typography>} />
                        <FormControlLabel value="TrustCap" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Non-discretionary trust - spouse sole capital and income beneficiary</Typography>} />
                        <FormControlLabel value="TrustInc" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Non-discretionary trust - spouse sole income beneficiary</Typography>} />
                        <FormControlLabel value="None" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">None</Typography>} />
                    </RadioGroup>
                </ModalFormRow>

            </Stack>
        </TabPanel>

        {/* ================= TAB 2: VALUES ================= */}
        <TabPanel value={tabValue} index={1}>
            <Stack spacing={0.5}>
                
                <ModalFormRow label="Value of property as reflected in the Inventory" required>
                    <TextField 
                        name="inventoryValue"
                        onChange={handleChange}
                        value={formData.inventoryValue || '0.00'} 
                        sx={{ width: '150px', bgcolor: '#fffde7', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>Property Value as reflected in the L&D Account</Typography>}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <TextField 
                                name="ldValue"
                                onChange={handleChange}
                                value={formData.ldValue || '0.00'} 
                                sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                                size="small" 
                                variant="outlined" 
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(If sold only complete the sale price below)</Typography>
                        </Grid>
                    </Grid>
                </ModalFormRow>

                <ModalFormRow label="Municipal Value of Land only">
                    <TextField 
                        name="municipalValueLand" value={formData.municipalValueLand || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="Municipal Value of Buildings only">
                    <TextField 
                        name="municipalValueBuildings" value={formData.municipalValueBuildings || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="If property was sold by Estate - Buyer's Full Names">
                    <TextField name="buyerFullName" value={formData.buyerFullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="If property was sold by Estate - Date Sold">
                    <TextField type="date" InputLabelProps={{ shrink: true }} name="dateSold" value={formData.dateSold || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>If property was sold by Estate - Sale Price</Typography>}>
                     <TextField 
                        name="salePrice" value={formData.salePrice || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="LandBank Value for Estate Duty purposes">
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <TextField 
                                name="landBankValue" value={formData.landBankValue || '0.00'} onChange={handleChange}
                                sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                                size="small" 
                                variant="outlined" 
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(Leave these two fields BLANK if asset value</Typography>
                        </Grid>
                    </Grid>
                </ModalFormRow>

                <ModalFormRow label="Value approved by SARS for Duty">
                     <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <TextField 
                                name="sarsValue" value={formData.sarsValue || '0.00'} onChange={handleChange}
                                sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                                size="small" 
                                variant="outlined" 
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>and duty are to be calculated on the L&D value)</Typography>
                        </Grid>
                    </Grid>
                </ModalFormRow>

                {/* Insurance Section */}
                <Box sx={{ mt: 4 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>Type of insurance:</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <RadioGroup row name="insuranceType" value={formData.insuranceType || 'None'} onChange={handleChange}>
                                <FormControlLabel value="Private" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Private</Typography>} />
                                <FormControlLabel value="Commercial" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Commercial</Typography>} />
                                <FormControlLabel value="SBIB" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">SBIB</Typography>} />
                                <FormControlLabel value="None" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">None</Typography>} />
                            </RadioGroup>
                        </Grid>
                    </Grid>
                </Box>

                <ModalFormRow label="Insurance Value:">
                     <TextField 
                        name="insuranceValue" value={formData.insuranceValue || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="Original value of property for valuation purposes">
                     <TextField 
                        name="originalValuation" value={formData.originalValuation || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <Box sx={{ mt: 4 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>PLEASE NOTE:</Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Any income received or expenses incurred on this property after date of death is to be captured in the estate current account cashbook and linked to this asset from there.
                    </Typography>
                </Box>

            </Stack>
        </TabPanel>

        {/* ================= TAB 3: NOTES ================= */}
        <TabPanel value={tabValue} index={2}>
            <Stack spacing={2}>
                
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#333', mb: 1, display: 'block' }}>
                        Notes about this asset to be printed on the L&D account:
                    </Typography>
                    <TextField 
                        name="notes" value={formData.notes || ''} onChange={handleChange}
                        fullWidth 
                        multiline 
                        rows={15} 
                        size="small" 
                        variant="outlined" 
                        sx={{ bgcolor: '#fff' }}
                    />
                </Box>

                <Stack spacing={0.5}>
                    <ModalFormRow label="Print above notes on the L&D a/c?">
                        <Checkbox name="printNotes" checked={formData.printNotes || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Must this asset be excluded from the community estate?">
                        <Checkbox name="excludeFromCommunity" checked={formData.excludeFromCommunity || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>
                </Stack>

            </Stack>
        </TabPanel>

        {/* ================= TAB 4: EST DUTY (1) ================= */}
        <TabPanel value={tabValue} index={3}>
            <Stack spacing={0.5}>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                    For Liquidation and Distribution purposes as well as to calculate Estate Duty:
                </Typography>

                <ModalFormRow label="Was this property donated mortis causa?">
                    <Checkbox name="donatedMortisCausa" checked={formData.donatedMortisCausa || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Was it donated by/inherited from Non-SA resident?">
                    <Checkbox name="donatedByNonResident" checked={formData.donatedByNonResident || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Was it donated to Deceased by its beneficiary?">
                    <Checkbox name="donatedToDeceased" checked={formData.donatedToDeceased || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Does this NOT belong to the deceased, but he could sell it?">
                    <Checkbox name="notBelongToDeceased" checked={formData.notBelongToDeceased || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="If so, who is the owner?">
                    <TextField name="ownerName" value={formData.ownerName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Was Duty paid on this asset in a previous Estate?">
                     <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox name="dutyPaidPreviousEstate" checked={formData.dutyPaidPreviousEstate || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(To qualify for a varying rebate of successive deaths)</Typography>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Was it acquired before ordinarily resident in SA?">
                    <Checkbox name="acquiredBeforeResident" checked={formData.acquiredBeforeResident || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Was it donated by Deceased to a charity/fund?">
                     <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox name="donatedToCharity" checked={formData.donatedToCharity || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(This includes a public educational or religious institution)</Typography>
                     </Stack>
                </ModalFormRow>

                {/* Complex Row: Donation Details */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                    <Grid item xs={12} md={4}>
                         <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mt: 1 }}>
                            Was this property donated by the Deceased?
                         </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                             <TextField 
                                name="donationTaxPaidAmount" value={formData.donationTaxPaidAmount || '0.00'} onChange={handleChange}
                                sx={{ width: '100px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                                size="small" 
                                variant="outlined" 
                            />
                            
                            <Typography variant="caption" sx={{ ml: 2 }}>Tax Paid</Typography>
                            <Checkbox name="donationTaxPaid" checked={formData.donationTaxPaid || false} onChange={handleChange} size="small" sx={{ p: 0 }} />

                            <Typography variant="caption" sx={{ ml: 2 }}>If so, when?</Typography>
                            <TextField 
                                name="donationTaxPaidDate" value={formData.donationTaxPaidDate || ''} onChange={handleChange}
                                sx={{ width: '100px', '& .MuiInputBase-input': { py: 0.5 } }} 
                                size="small" 
                                variant="outlined" 
                            />
                        </Stack>
                    </Grid>
                </Grid>

                <ModalFormRow label="Value of property">
                    <TextField 
                        name="propertyValue" value={formData.propertyValue || '0.00'} onChange={handleChange}
                        sx={{ width: '100px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="If so, to whom was it donated?">
                    <TextField name="donatedToWhom" value={formData.donatedToWhom || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Beneficiary becomes owner by nomination agreement?">
                    <Checkbox name="nominationAgreement" checked={formData.nominationAgreement || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Farming property as per A(i) of the Estate Duty Return?">
                    <Checkbox name="farmingProperty" checked={formData.farmingProperty || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Has the heir made any improvements to the property?">
                     <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox name="heirImprovements" checked={formData.heirImprovements || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(Section 4(i))</Typography>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Value of improvements made">
                    <TextField 
                        name="improvementsValue" value={formData.improvementsValue || '0.00'} onChange={handleChange}
                        sx={{ width: '100px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

            </Stack>
        </TabPanel>

        {/* ================= TAB 5: EST DUTY (2) ================= */}
        <TabPanel value={tabValue} index={4}>
            <Stack spacing={0.5}>

                <ModalFormRow label="Is usufruct/fiduciary or such like right being CREATED now?">
                    <Checkbox name="usufructCreated" checked={formData.usufructCreated || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="In favour of whom?">
                     <Stack direction="row" spacing={1} sx={{ width: '100%' }} alignItems="flex-start">
                         <TextField name="usufructInFavourOf" value={formData.usufructInFavourOf || ''} onChange={handleChange} fullWidth multiline rows={4} size="small" variant="outlined" sx={{ bgcolor: '#fff' }} />
                         <Button variant="contained" size="small" sx={{ bgcolor: '#bdbdbd', color: '#333', textTransform: 'none', minWidth: '80px', height: '40px', alignSelf: 'flex-start' }}>
                            Heirs
                         </Button>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Yield at which value of right must be calculated">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TextField 
                            name="usufructYield" value={formData.usufructYield || '12.000000'} onChange={handleChange}
                            sx={{ width: '100px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Typography variant="caption">%</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Period, if fixed, for which right is to be held">
                    <TextField 
                        name="usufructPeriod" value={formData.usufructPeriod || '0'} onChange={handleChange}
                        sx={{ width: '80px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="If so, value of such right ito Sect. 5 (in figures)">
                     <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField 
                            name="usufructSect5Value" value={formData.usufructSect5Value || '0.00'} onChange={handleChange}
                            sx={{ width: '120px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#bdbdbd', color: '#333', textTransform: 'none', minWidth: '80px' }}>
                            Table A
                        </Button>
                        <Button variant="contained" size="small" sx={{ bgcolor: '#bdbdbd', color: '#333', textTransform: 'none', minWidth: '80px' }}>
                            Table B
                        </Button>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="How was the property originally acquired? (if known)">
                    <TextField name="originallyAcquired" value={formData.originallyAcquired || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="On which amount was transfer duty paid? (if known)">
                    <TextField 
                        name="transferDutyAmount" value={formData.transferDutyAmount || '0.00'} onChange={handleChange}
                        sx={{ width: '120px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="If it wasn't taken as 4q deduction in predeceased's estate, are you taking it as 4m deduction in this estate?">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', pr: 4 }}>
                        <Checkbox name="not4qDeduction" checked={formData.not4qDeduction || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </Box>
                </ModalFormRow>

                <Box sx={{ mt: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>PLEASE NOTE:</Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 2, fontWeight: 'bold' }}>
                        Information on EXISTING rights (usufruct/fiduciary etc) are to be recorded by clicking on the
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 2, fontWeight: 'bold' }}>
                        42(2)/Rights button below.
                    </Typography>
                </Box>

            </Stack>
        </TabPanel>

       {/* ================= TAB 6: ESTIMATED CGT ================= */}
        <TabPanel value={tabValue} index={5}>
            <Stack spacing={0.5}>

                <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                    <Grid item xs={12} md={4}>
                         <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mt: 1 }}>
                            Is this asset subject to Capital Gains Tax?
                         </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                             <Checkbox name="subjectToCGT" checked={formData.subjectToCGT || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                             <Typography variant="caption" sx={{ fontWeight: 'bold' }}>(For info refer to www.sars.gov.za or press F1)</Typography>
                        </Stack>
                    </Grid>
                </Grid>

                <ModalFormRow label="Valuation as at 1 October 2001">
                    <TextField 
                        name="valuation2001" value={formData.valuation2001 || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="What is the calculated roll over base cost for this asset?">
                    <TextField 
                        name="rollOverBaseCost" value={formData.rollOverBaseCost || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="Total exclusions (NOT the annual exclusion)">
                    <TextField 
                        name="totalExclusions" value={formData.totalExclusions || '0.00'} onChange={handleChange}
                        sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        size="small" 
                        variant="outlined" 
                    />
                </ModalFormRow>

                <ModalFormRow label="Amount of Capital Gain (if applicable)">
                     <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField 
                            name="capitalGain" value={formData.capitalGain || '0.00'} onChange={handleChange}
                            sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '80px', boxShadow: 'none', border: '1px solid #ccc' }}>
                            Calculate
                        </Button>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Amount of Capital Loss (if applicable)">
                     <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField 
                            name="capitalLoss" value={formData.capitalLoss || '0.00'} onChange={handleChange}
                            sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '80px', boxShadow: 'none', border: '1px solid #ccc' }}>
                            Calculate
                        </Button>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Asset Type Source Code">
                     <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField 
                            name="assetTypeSourceCode" value={formData.assetTypeSourceCode || ''} onChange={handleChange}
                            sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Typography variant="caption">(e.g. 4250 or 4251)</Typography>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Net Capital Gain/Loss">
                     <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField 
                            name="netCapitalGainLoss" value={formData.netCapitalGainLoss || '0.00'} onChange={handleChange}
                            sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '80px', boxShadow: 'none', border: '1px solid #ccc' }}>
                            Calculate
                        </Button>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Inclusion Rate">
                     <Stack direction="row" alignItems="center" spacing={1}>
                        <TextField 
                            name="inclusionRate" value={formData.inclusionRate || '0.00'} onChange={handleChange}
                            sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>%</Typography>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Taxable Capital Gain/Loss">
                     <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField 
                            name="taxableCapitalGainLoss" value={formData.taxableCapitalGainLoss || '0.00'} onChange={handleChange}
                            sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '80px', boxShadow: 'none', border: '1px solid #ccc' }}>
                            Calculate
                        </Button>
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="What was the Original Purchase Price of the Asset?">
                     <Stack direction="row" alignItems="center" spacing={2}>
                        <TextField 
                            name="originalPurchasePrice" value={formData.originalPurchasePrice || '0.00'} onChange={handleChange}
                            sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                        <Typography variant="caption">Date purchased</Typography>
                        <TextField 
                            type="date" InputLabelProps={{ shrink: true }} name="datePurchased" value={formData.datePurchased || ''} onChange={handleChange}
                            sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} 
                            size="small" 
                            variant="outlined" 
                        />
                     </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the method of valuation">
                    <TextField name="methodOfValuation" value={formData.methodOfValuation || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Name of Valuer">
                    <TextField name="nameOfValuer" value={formData.nameOfValuer || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Complete Valuer's Contact Details">
                    <TextField name="valuerContactDetails" value={formData.valuerContactDetails || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

            </Stack>
        </TabPanel>

      </DialogContent>

      {/* ================= DIALOG FOOTER ================= */}
      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0', justifyContent: 'space-between' }}>
         <Box /> {/* Left spacer */}
         
         <Stack direction="row" spacing={2}>
             <Button 
                onClick={handleSave} 
                variant="contained" 
                color="success" 
                startIcon={<span>✓</span>} 
                sx={{ textTransform: 'none', minWidth: '100px', fontWeight: 'bold' }}
             >
                OK
             </Button>
             <Button 
                onClick={onClose} 
                variant="contained" 
                color="inherit" 
                startIcon={<span>X</span>} 
                sx={{ bgcolor: '#e0e0e0', color: '#333', textTransform: 'none', minWidth: '100px' }}
             >
                Cancel
             </Button>
         </Stack>

         {/* ACTION: Opens the Section 42(2) Existing Rights Modal */}
         <Button 
            onClick={handleOpenExistingRights}
            variant="contained" 
            color="inherit" 
            sx={{ 
                bgcolor: '#e0e0e0', 
                color: '#333', 
                textTransform: 'none', 
                minWidth: '150px', 
                fontSize: '0.75rem',
                border: '1px solid #ccc'
            }}
         >
             Sect 42(2) End/Existing Rights
         </Button>
      </DialogActions>

      {/* RENDER THE SECTION 42 SUB-MODAL */}
      <ExistingRightsDialog 
        open={openExistingRights} 
        onClose={handleCloseExistingRights}
        onSave={handleSaveExistingRights}
        initialData={formData.existingRights}
      />
    </Dialog>
  );
}